import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Loader from '@/components/Loader';
import { getDeliveryUpdates, useSendDeliveryUpdateEmail } from '@/services/deliveryService';
import { getMediaPresignedUrl } from '@/services/uploadMedia';
import type { DeliveryUpdateImage } from '@/types/deliveryUpdate.types';
import type { DeliveryUpdateDetail } from '@/types/delivery.types';
import { ArrowLeft, ArrowRight, CircleCheck } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import AlertBox from '@/components/AlertBox';
import { displayError } from '@/lib/error';
import { GenericErrorResponse } from '@brainvaulttech/dgr-react-sdk';

const mapUpdateMediaToImages = (
  update: DeliveryUpdateDetail,
  imageUrlsByMediaId: Record<string, string>
): DeliveryUpdateImage[] =>
  update.media
    ?.map((mediaWrapper) => {
      const mediaId = mediaWrapper.media.mediaId;
      const imageUrl = imageUrlsByMediaId[mediaId];
      if (!imageUrl) return null;

      return {
        mediaId,
        imageUrl,
        mimeType: mediaWrapper.media.mimeType,
      };
    })
    .filter((image): image is DeliveryUpdateImage => image !== null) ?? [];

const getUpdateImages = (
  updateId: string,
  updatesById: Record<string, DeliveryUpdateDetail>,
  imageUrlsByMediaId: Record<string, string>
): DeliveryUpdateImage[] => {
  const update = updatesById[updateId];
  return update ? mapUpdateMediaToImages(update, imageUrlsByMediaId) : [];
};

export const ShareDeliveryUpdateImageDialog = ({
  onCloseClick,
  updateIds,
  workOrderId,
  recipientEmail,
  onEmailSent,
}: {
  onCloseClick: () => void;
  updateIds: string[];
  workOrderId: string;
  recipientEmail: string;
  onEmailSent?: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [updatesById, setUpdatesById] = useState<Record<string, DeliveryUpdateDetail>>({});
  const [imageUrlsByMediaId, setImageUrlsByMediaId] = useState<Record<string, string>>({});
  const [selectedImages, setSelectedImages] = useState<Record<string, string[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [displayConfirmationDialog, setDisplayConfirmationDialog] = useState(false);

  const currentUpdateId = updateIds[currentIndex] ?? '';
  const sendDeliveryUpdateEmail = useSendDeliveryUpdateEmail();

  useEffect(() => {
    const fetchUpdates = async () => {
      if (!workOrderId) return;

      setIsLoading(true);
      const result = await getDeliveryUpdates(workOrderId);

      if (result.data?.updates) {
        const byId = Object.fromEntries(
          result.data.updates.map((update) => [update.updateId, update])
        );
        setUpdatesById(byId);
        setSelectedImages((prev) => {
          const next = { ...prev };
          result.data?.updates.forEach((update) => {
            if (!next[update.updateId]) {
              next[update.updateId] = [];
            }
          });
          return next;
        });

        const mediaIds = [
          ...new Set(
            result.data.updates.flatMap(
              (update) => update.media?.map((mediaWrapper) => mediaWrapper.media.mediaId) ?? []
            )
          ),
        ];

        const urlEntries = await Promise.all(
          mediaIds.map(async (mediaId) => {
            const url = await getMediaPresignedUrl(mediaId);
            return url ? ([mediaId, url] as const) : null;
          })
        );

        setImageUrlsByMediaId(
          Object.fromEntries(urlEntries.filter((entry): entry is readonly [string, string] => entry !== null))
        );
      } else if (result.error) {
        displayError(result.error, 5000);
      }

      setIsLoading(false);
    };

    void fetchUpdates();
  }, [workOrderId]);

  useEffect(() => {
    setSelectedImages((prev) => ({ ...prev, [currentUpdateId]: prev[currentUpdateId] || [] }));
  }, [currentUpdateId]);

  const updateImages = useMemo(
    () => getUpdateImages(currentUpdateId, updatesById, imageUrlsByMediaId),
    [currentUpdateId, updatesById, imageUrlsByMediaId]
  );

  const nextUpdateId = updateIds[currentIndex + 1];
  const hasNextPageWithImages = nextUpdateId
    ? getUpdateImages(nextUpdateId, updatesById, imageUrlsByMediaId).length > 0
    : false;

  const currentSelectedImages = selectedImages[currentUpdateId] || [];

  const toggleImageSelect = (imageId: string) => {
    setSelectedImages((prev) => {
      const prevSelected = prev[currentUpdateId] || [];
      const updated = prevSelected.includes(imageId)
        ? prevSelected.filter((id) => id !== imageId)
        : [...prevSelected, imageId];
      return { ...prev, [currentUpdateId]: updated };
    });
  };

  const handleSelectAll = () => {
    if (!updateImages.length) return;
    setSelectedImages((prev) => ({
      ...prev,
      [currentUpdateId]:
        currentSelectedImages.length === updateImages.length
          ? []
          : updateImages.map((img) => img.mediaId),
    }));
  };

  const handleSendEmail = () => {
    sendDeliveryUpdateEmail.mutate(
      { workOrderId, imageIds: Object.values(selectedImages).flat() },
      {
        onSuccess: (data) => {
          toast({
            description: (data as { message: string })?.message || 'Email will be sent shortly',
          });
          setDisplayConfirmationDialog(false);
          onEmailSent?.();
          onCloseClick();
        },
        onError: (error) => {
          displayError(error as unknown as GenericErrorResponse, 5000);
          setDisplayConfirmationDialog(false);
          onCloseClick();
        },
      }
    );
  };

  return (
    <Dialog open={true} onOpenChange={onCloseClick}>
      <DialogContent className="max-w-[50vw] h-[80vh] bg-white flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex justify-between items-center">
            <div className="flex w-full flex-col gap-3">
              <h2 className="text-lg font-semibold mb-1">Select Images</h2>
              <p className="text-sm text-gray-500">Select the images to include in the delivery update email</p>
              <div className="flex flex-row justify-between">
                <p className="text-sm font-medium mb-2 text-primary">Update {currentIndex + 1} Images:</p>
                {updateImages.length > 0 && (
                  <span
                    onClick={handleSelectAll}
                    className="text-sm text-primary font-medium hover:underline border-0 hover:bg-none cursor-pointer"
                  >
                    {currentSelectedImages.length === updateImages.length ? 'Unselect All' : 'Select All'}
                  </span>
                )}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <Loader isLoading={isLoading}>
            {!updateImages.length ? (
              <div className="text-center py-8 text-gray-500">No images found for this update.</div>
            ) : (
              <div className="flex flex-col h-full">
                <div
                  className="grid gap-4 pr-8 pb-[20px] custom-scrollbar"
                  style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}
                >
                  {updateImages.map((image) => {
                    const isSelected = currentSelectedImages.includes(image.mediaId);

                    return (
                      <div key={image.mediaId} className="w-full">
                        <div
                          onClick={() => toggleImageSelect(image.mediaId)}
                          className="relative w-fit rounded-lg border cursor-pointer transition-all"
                        >
                          <div className="relative flex">
                            <img
                              src={image.imageUrl}
                              alt="delivery update"
                              className="object-cover flex flex-1 h-32 rounded-md"
                            />
                            {isSelected && (
                              <div className="absolute -top-3 -right-3 z-50 bg-white rounded-full shadow-md p-[2px]">
                                <CircleCheck fill="#1B8652" color="#fff" size={20} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Loader>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-3 flex !flex-col gap-2">
          <div className="flex w-full mt-4 justify-between items-center">
            {currentIndex !== 0 && (
              <div
                className="gap-2 flex items-center text-sm cursor-pointer text-primary"
                onClick={() => setCurrentIndex((i) => i - 1)}
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Previous</span>
              </div>
            )}

            <div className="flex-1" />

            {hasNextPageWithImages && (
              <div
                className="gap-2 flex items-center text-sm cursor-pointer text-primary ml-auto"
                onClick={() => setCurrentIndex((i) => i + 1)}
              >
                <span>Next</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            )}
          </div>

          <div className="flex flex-row gap-4 justify-end w-full mt-2">
            <Button variant="outline" className="h-8" onClick={onCloseClick}>
              Cancel
            </Button>
            <Button
              className="h-8"
              onClick={() => {
                setDisplayConfirmationDialog(true);
              }}
              isLoading={sendDeliveryUpdateEmail.isPending}
            >
              Email Delivery Updates
            </Button>
          </div>
          {displayConfirmationDialog && (
            <AlertBox
              title="Are you sure?"
              description={`Are you sure you want to share the status update with ${recipientEmail}?`}
              open={displayConfirmationDialog}
              cancelBtnLabel="No"
              confirmBtnLabel="Yes"
              isLoading={sendDeliveryUpdateEmail.isPending}
              cancelBtnClick={() => {
                setDisplayConfirmationDialog(false);
              }}
              confirmBtnClick={() => {
                handleSendEmail();
              }}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
